// FTUE-v2 anonymous reveal read (capability `boat-reveal`, design D3/D3′).
//
// Polls the token-scoped `GET /v1/reveal/snapshot` with `Authorization: Reveal
// <revealToken>` and returns the ALLOW-LISTED projection of the boat's pre-claim
// discovery snapshot — no account required. The reveal token is the read-scoped
// capability from the boat's handoff URL fragment; it cannot claim or redeem.

import { useEffect, useRef, useState } from 'react';

import { api } from '../api';

/** One discovered candidate in the anonymous projection (no addressing/evidence). */
export interface RevealProjectionCandidate {
  kind: string;
  vendor?: string;
  model?: string;
  confidence?: number;
  requiresCredentials?: boolean;
}

/** The allow-listed projection returned by GET /v1/reveal/snapshot. */
export interface RevealProjection {
  sessionId: string;
  status: string;
  phase: string;
  candidateCount: number;
  gatewayCount: number;
  candidates: RevealProjectionCandidate[];
  emittedAt: string;
}

export type RevealSnapshotState =
  | { kind: 'loading' }
  | { kind: 'waiting' } // valid token, boat hasn't mirrored yet (404)
  | { kind: 'ready'; projection: RevealProjection }
  | { kind: 'claimed' } // this boat was already claimed (410 already_claimed)
  | { kind: 'expired' } // token invalid / legacy TTL
  | { kind: 'error'; message: string };

const POLL_MS = 2500;

/**
 * Poll the anonymous reveal snapshot for a reveal token. Keeps polling while the
 * boat is still discovering (so the gateway list fills in live), backing off to
 * a slower steady cadence once a snapshot has landed. `null`/empty token short-
 * circuits to `expired` (nothing to show).
 */
export function useRevealSnapshot(revealToken: string | null): RevealSnapshotState {
  const [state, setState] = useState<RevealSnapshotState>({ kind: 'loading' });
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!revealToken) {
      setState({ kind: 'expired' });
      return;
    }
    let active = true;

    const poll = async (): Promise<void> => {
      try {
        const projection = await api<RevealProjection>('/v1/reveal/snapshot', {
          headers: { authorization: `Reveal ${revealToken}` },
        });
        if (!active) return;
        setState({ kind: 'ready', projection });
      } catch (err) {
        if (!active) return;
        const e = err as { status?: number; body?: { error?: string } };
        if (e.status === 404) {
          // Valid token, boat hasn't mirrored a snapshot yet — keep waiting
          // (the boat re-pushes on boot and on a heartbeat, so this recovers
          // by itself once the boat comes online).
          setState((prev) => (prev.kind === 'ready' ? prev : { kind: 'waiting' }));
        } else if (e.status === 410 && e.body?.error === 'already_claimed') {
          setState({ kind: 'claimed' });
        } else if (e.status === 401) {
          setState({ kind: 'expired' });
        } else {
          setState({ kind: 'error', message: 'Could not reach your boat. Retrying…' });
        }
      } finally {
        if (active) timer.current = setTimeout(() => void poll(), POLL_MS);
      }
    };

    void poll();
    return () => {
      active = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, [revealToken]);

  return state;
}
